import React, { useContext, useMemo, memo } from 'react';
import isEmpty from 'lodash/isEmpty';
import PropTypes from 'prop-types';
import {
    NavigationContext,
} from '../context/NavigationContext';
import { LoyaltyComponent, StyledAccordionLabel, StyledLinkLabel, PreIcon, PostIcon } from './components/LoyaltyStyled';
import { getPathToRoute } from '../util/getPathToRoute';
import getStyles from '../util/getStyles';

const defaultStyles = {
    button: {
        lineHeight: 1,
        display: 'flex',
        textDecoration: 'none',

        borderWidth: 0,
        borderStyle: 'solid',
        borderColor: 'transparent',
    },
    preIcon: { height: '1em', color: '#dddddd' },
    postIcon: { height: '1em', color: '#dddddd' },
    dropdown: { borderStyle: 'solid', borderColor: 'transparent', borderWidth: 0 },
    label: { display: 'flex', alignItems: 'center', textDecoration: 'none', padding: '4px 8px' },
};

const Loyalty = ({
    id,
    children,
    styles = {},
    attributes: {
        label,
        preIcon,
        postIcon,
        postIconActiveRotation = '0deg',
        fontSelection = '',
    },
    structureType,
    isChildButton,
    ...props
}) => {
    const {
        routesMap,
        state: { activeAccordionId },
        user: { isLoyaltyActive, loyalty: { pointBalance } = {} } = {},
        paths: { loyalty } = {},
    } = useContext(NavigationContext);
    const hasChildren = typeof children === 'object' && !isEmpty(children);
    const isMobileCollapsible = hasChildren && structureType === 'mobile';
    const isOpen = activeAccordionId === id || String(activeAccordionId).startsWith(id);
    const loyaltyPointsUrl = getPathToRoute({
        routesMap,
        path: loyalty || '',
    });

    const { rootStyles = {}, rootHoverStyles = {}, childrenStyles = {} } = useMemo(() => getStyles(styles));

    const labelStyles = {
        ...defaultStyles.label,
        ...childrenStyles.label,
    };
    const Label = isMobileCollapsible ? StyledAccordionLabel : StyledLinkLabel;

    const postIconStyles = { ...defaultStyles.postIcon, ...childrenStyles.postIcon };
    const preIconStyles = { ...defaultStyles.preIcon, ...childrenStyles.preIcon };

    const buttonStyles = {
        ...defaultStyles.button,
        ...rootStyles,
        ...(structureType === 'mobile' && { flexShrink: 0 }),
        '&:hover >': {
            ...rootStyles['&:hover'],
            [StyledAccordionLabel]: {
                ...rootHoverStyles.label,
                [PostIcon]: rootHoverStyles.postIcon,
                [PreIcon]: rootHoverStyles.preIcon,
            },
            [StyledLinkLabel]: {
                ...rootHoverStyles.label,
                [PostIcon]: rootHoverStyles.postIcon,
                [PreIcon]: rootHoverStyles.preIcon,
            },
        },
    };

    return (
        <>
            {isLoyaltyActive
                && (
                    <LoyaltyComponent
                        id={`btn_${id}`}
                        key={`btn_${id}`}
                        postIconActiveRotation={postIconActiveRotation}
                        {...props}
                        className={`${fontSelection} ${isMobileCollapsible ? 'collapsible' : ''} ${
                            isOpen ? 'open' : ''
                        }`}
                        styles={buttonStyles}
                    >
                        <Label
                            to={loyaltyPointsUrl}
                            styles={labelStyles}
                        >
                            <PreIcon icon={preIcon} styles={preIconStyles} />
                            {label}
                            :
                            <p>{pointBalance}</p>
                            <PostIcon icon={postIcon} styles={postIconStyles} />
                        </Label>
                    </LoyaltyComponent>
                )}
        </>
    );
};

Loyalty.propTypes = {
    id: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
    ]),
    styles: PropTypes.object,
    children: PropTypes.node,
    attributes: PropTypes.object,
    structureType: PropTypes.string,
    isChildButton: PropTypes.bool,
};

export default memo(Loyalty);
